import { usePermissions } from '@/shared/permissions';

import { CATALOG_PERMISSIONS } from '../model/permissions';

export interface CatalogPermissions {
  /** Browse the catalog (products, categories, media, import, search). */
  canView: boolean;
  /** Create/edit catalog data. */
  canManage: boolean;
  /** Read price governance (config, rules, approvals, policy, audit). */
  canPricingView: boolean;
  /** Manage price governance. */
  canPricingManage: boolean;
  /** Admin tier (`settings.pricing.manage`) — price-list store bind/unbind + list activate/deactivate. */
  canPricingAdmin: boolean;
  /** `catalog.pricing.bulk` — mass-change actions (batch dry-run/apply, markdown activate/cancel);
   * required IN ADDITION TO `canPricingManage`, not instead of it. */
  canPricingBulk: boolean;
  /** Read PIM structure: attribute dictionary, product families, measurement families, languages. */
  canStructureView: boolean;
  /** Manage PIM structure (create/edit/delete + move categories). */
  canStructureManage: boolean;
  /** Read the publication workspace: catalogs, rules, sales-channel bindings, catalog-type profiles. */
  canPublicationView: boolean;
  /** Manage the publication workspace. */
  canPublicationManage: boolean;
}

/** Resolves the catalog permission flags from the session permissions. */
export function useCatalogPermissions(): CatalogPermissions {
  const { hasPermission } = usePermissions();
  return {
    canView: hasPermission(CATALOG_PERMISSIONS.catalog.view),
    canManage: hasPermission(CATALOG_PERMISSIONS.catalog.manage),
    canPricingView: hasPermission(CATALOG_PERMISSIONS.pricing.view),
    canPricingManage: hasPermission(CATALOG_PERMISSIONS.pricing.manage),
    canPricingAdmin: hasPermission(CATALOG_PERMISSIONS.pricingAdmin.manage),
    canPricingBulk: hasPermission(CATALOG_PERMISSIONS.pricingBulk.manage),
    canStructureView: hasPermission(CATALOG_PERMISSIONS.structure.view),
    canStructureManage: hasPermission(CATALOG_PERMISSIONS.structure.manage),
    canPublicationView: hasPermission(CATALOG_PERMISSIONS.publication.view),
    canPublicationManage: hasPermission(CATALOG_PERMISSIONS.publication.manage),
  };
}
